import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TaskActionsResponse } from 'api/models';
import { TaskActionTypeSchema } from 'app/Private/Clients/Detail/TaskTab/types/task-action-type';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import type { Task, TaskListResponse } from './use-load-tasks';
import { type ApiError, useLegacyAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export const UpdateTaskStatusVariablesSchema = z.object({
  task_ids: z.array(z.string()),
  action_type: TaskActionTypeSchema,
});

export type UpdateTaskStatusVariables = z.infer<typeof UpdateTaskStatusVariablesSchema>;

export async function updateTaskStatusFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateTaskStatusVariables,
): Promise<TaskActionsResponse> {
  const instance = await axiosInstance;
  const response = await instance.post('/tasks/update_status', variables);

  return response.data;
}

export function useUpdateTaskStatus() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  type MutationContext = {
    previousQueries: Array<[queryKey: readonly unknown[], data: TaskListResponse | undefined]>;
  };

  const mutation = useMutation<
    TaskActionsResponse,
    ApiError,
    UpdateTaskStatusVariables,
    MutationContext
  >({
    mutationKey: ['updateTaskStatus'],
    mutationFn: variables => updateTaskStatusFn(axiosInstance, variables),
    // Optimistic update: task status changes reflect instantly in the UI without waiting for the server,
    // keeping the list responsive when actions are performed in quick succession.
    onMutate: async variables => {
      await client.cancelQueries({ queryKey: ['tasks'] });

      const previousQueries = client.getQueriesData<TaskListResponse>({
        queryKey: ['tasks'],
      });

      client.setQueriesData<TaskListResponse>({ queryKey: ['tasks'] }, old => {
        if (!old) return old;

        return {
          ...old,
          data: old.data.map(task => applyOptimisticUpdate(task, variables)),
        };
      });

      return { previousQueries };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousQueries)
        for (const [queryKey, data] of context.previousQueries) client.setQueryData(queryKey, data);
    },
    onSettled: () => client.invalidateQueries({ queryKey: ['tasks'] }),
  });

  return addActionNameToMutationResult<
    TaskActionsResponse,
    ApiError,
    UpdateTaskStatusVariables,
    'updateTaskStatus'
  >('updateTaskStatus', mutation);
}

function applyOptimisticUpdate(task: Task, variables: UpdateTaskStatusVariables): Task {
  if (!variables.task_ids.includes(String(task.id))) return task;

  switch (variables.action_type) {
    case 'complete':
      return { ...task, completed: true, status_key: 'done' };
    case 'revert':
      return { ...task, completed: false, skipped: false, status_key: 'to_do' };
    case 'skip':
      return { ...task, skipped: true, status_key: 'skipped' };
    default:
      return task;
  }
}
